import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {
  readTopRepositories,
  RepositoryPicker,
  type RepositoryPickerRepository,
} from '@github-ui/item-picker/RepositoryPicker'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {BranchPicker, type BranchPickerBranch} from '@github-ui/item-picker-branch/BranchPicker'
import {DefaultBranchPickerAnchor} from '@github-ui/item-picker-branch/DefaultBranchPickerAnchor'
import {ISSUE_VIEWER_CACHE_TTL_MS} from '@github-ui/preheat-issues-manager/issue-viewer-storage-key'
import {useQuery} from '@github-ui/react-query'
import {createServerPersistedQueryPersister} from '@github-ui/react-query-persister'
import {
  CreateBranchDialogFindRepoBranchesServerQuery,
  CreateBranchDialogReposServerQuery,
} from '@github-ui/server-persisted-queries'
import type {CreateBranchDialogFindRepoBranchesServerQueryQuery} from '@github-ui/server-persisted-queries/types/CreateBranchDialogFindRepoBranchesServerQuery'
import type {CreateBranchDialogReposServerQueryQuery} from '@github-ui/server-persisted-queries/types/CreateBranchDialogReposServerQuery'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import {CopyIcon, GitBranchIcon, SyncIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, FormControl, Radio, RadioGroup, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {Suspense, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import {
  type CreateLinkedBranchRequest,
  prepareCreateLinkedBranch,
  useCreateLinkedBranchMutation,
} from '../../../mutations/create-linked-branch-mutation'
import {RepositoryPickerPlaceholder} from '../../RepositoryPlaceholder'
import styles from './CreateBranchDialog.module.css'
import {readRef} from './DevelopmentSection'
import {
  createBranchDialogBranchesQueryKey,
  fetchCreateBranchDialogBranches,
} from './fetch-create-branch-dialog-branches'
import {createBranchDialogReposQueryKey, fetchCreateBranchDialogRepos} from './fetch-create-branch-dialog-repos'
import {generateSuggestedBranchName} from './utils'

export type CreateBranchDialogProps = {
  issueId: string
  title: string
  number: number
  owner: string
  repo: string
  onClose: (nextStep: BranchNextStep, branchName: string | null) => void
  /** Starts creation in the parent and dismisses this dialog immediately. */
  onSubmit?: (request: CreateLinkedBranchRequest, nextStep: BranchNextStep) => void
  returnFocusRef?: React.RefObject<HTMLElement | null>
}

export type CreateBranchDialogInternalProps = {
  issueId: string
  owner: string
  repo: string
  branchSourceName: string | null
  selectedBranchName: string
  onClose: (nextStep: BranchNextStep, branchName: string | null) => void
  setCreateBranchInput: (request: CreateLinkedBranchRequest | null) => void
  setBranchSourceName: (name: string | null) => void
}

export type BranchNextStep = 'none' | 'local' | 'codespace' | 'desktop'

// Local BranchPickerPlaceholder component
const BranchPickerPlaceholder = ({currentBranch}: {currentBranch?: string | null}) => (
  <Button leadingVisual={GitBranchIcon} trailingVisual={TriangleDownIcon} disabled>
    {currentBranch ?? 'Select branch'}
  </Button>
)

export const CreateBranchDialog = ({
  title,
  number,
  owner,
  repo,
  issueId,
  onClose,
  onSubmit,
  returnFocusRef,
  ...rest
}: CreateBranchDialogProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [createBranchInput, setCreateBranchInput] = useState<CreateLinkedBranchRequest | null>(null)
  const {addToast} = useToastContext()
  const [branchSourceName, setBranchSourceName] = useState<string | null>(null)
  const suggestedBranchName = useMemo(() => generateSuggestedBranchName(number, title), [number, title])
  const [selectedBranchName, setSelectedBranchName] = useState<string>(suggestedBranchName)
  const {mutate: createLinkedBranchTanStack} = useCreateLinkedBranchMutation({owner, repo, number, issueId})

  const [branchNextStep, setBranchNextStep] = useState<BranchNextStep>('local')
  const onChangeNextStep = useCallback((value: string | null) => {
    switch (value) {
      case 'codespace':
        setBranchNextStep('codespace')
        break
      case 'desktop':
        setBranchNextStep('desktop')
        break
      case 'local':
        setBranchNextStep('local')
        break
      default:
        setBranchNextStep('none')
    }
  }, [])

  const onCreateBranchError = useCallback(
    (error: Error) => {
      setIsLoading(false)
      const message = error.name === 'LinkedBranchExists' ? error.message : ERRORS.couldNotCreateBranch
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: 'error',
        message,
      })
    },
    [addToast],
  )

  const onCreateBranchCompleted = useCallback(
    (branchName: string | null) => {
      setIsLoading(false)
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: 'success',
        message: 'Branch created',
      })
      onClose(branchNextStep, branchName)
    },
    [addToast, branchNextStep, onClose],
  )

  const onCreateBranch = useCallback(() => {
    if (!createBranchInput) return

    if (onSubmit) {
      onSubmit(createBranchInput, branchNextStep)
      onClose('none', null)
      return
    }

    setIsLoading(true)
    createLinkedBranchTanStack(prepareCreateLinkedBranch(createBranchInput), {
      onError: onCreateBranchError,
      onSuccess: response => onCreateBranchCompleted(response.createLinkedBranch?.linkedBranch?.ref?.name ?? null),
    })
  }, [
    branchNextStep,
    createBranchInput,
    createLinkedBranchTanStack,
    onClose,
    onCreateBranchCompleted,
    onCreateBranchError,
    onSubmit,
  ])

  return (
    <Dialog
      returnFocusRef={returnFocusRef}
      width="large"
      height="auto"
      onClose={() => onClose('none', null)}
      title={'Create a branch for this issue'}
      renderFooter={() => (
        <>
          <Dialog.Footer className={styles.Dialog_Footer}>
            <RadioGroup name={'create-branch-next-action-group'} onChange={onChangeNextStep}>
              <RadioGroup.Label className={styles.RadioGroup_Label}>What&apos;s next?</RadioGroup.Label>
              <FormControl>
                <Radio name="create-branch-next-action" value="local" checked={branchNextStep === 'local'} />
                <FormControl.Label>Checkout locally</FormControl.Label>
              </FormControl>
              <FormControl>
                <Radio name="create-branch-next-action" value="desktop" />
                <FormControl.Label>Open branch with GitHub Desktop</FormControl.Label>
              </FormControl>
            </RadioGroup>
          </Dialog.Footer>
          <Dialog.Footer>
            <Button
              variant={'primary'}
              onClick={onCreateBranch}
              leadingVisual={isLoading ? SyncIcon : null}
              disabled={isLoading || !createBranchInput}
            >
              Create branch
            </Button>
          </Dialog.Footer>
        </>
      )}
    >
      <div data-testid={'branch-name'} className={styles.branchNameContainer}>
        <span className={styles.fieldLabel}>Branch name</span>
        <TextInput
          trailingAction={
            <CopyToClipboardIconButton
              textToCopy={selectedBranchName}
              ariaLabel={'Copy branch name to clipboard'}
              icon={CopyIcon}
              tooltipProps={{direction: 'w'}}
            />
          }
          value={selectedBranchName}
          onChange={e => setSelectedBranchName(e.target.value)}
          className={styles.TextInput}
        />
      </div>
      <Suspense fallback={<CreateBranchDialogInternalInputsLoading branchSourceName={branchSourceName} />}>
        <CreateBranchDialogInternal
          {...rest}
          issueId={issueId}
          owner={owner}
          repo={repo}
          branchSourceName={branchSourceName}
          onClose={onClose}
          selectedBranchName={selectedBranchName}
          setCreateBranchInput={setCreateBranchInput}
          setBranchSourceName={setBranchSourceName}
        />
      </Suspense>
    </Dialog>
  )
}

const CreateBranchDialogInternal = ({repo, owner, branchSourceName, ...props}: CreateBranchDialogInternalProps) => {
  const {sendAnalyticsEvent} = useAnalytics()
  const persister = useMemo(
    () =>
      createServerPersistedQueryPersister({
        queryHash: CreateBranchDialogReposServerQuery,
        sendAnalyticsEvent,
        maxAgeMs: ISSUE_VIEWER_CACHE_TTL_MS,
        // Restored repositories may be stale within their TTL, so revalidate after restoring them.
        alwaysRefetch: true,
      }),
    [sendAnalyticsEvent],
  )
  const query = useQuery({
    queryKey: createBranchDialogReposQueryKey(owner, repo),
    queryFn: ({signal}) => fetchCreateBranchDialogRepos(owner, repo, signal),
    gcTime: ISSUE_VIEWER_CACHE_TTL_MS,
    persister,
  })

  if (query.isPending) return <CreateBranchDialogInternalInputsLoading branchSourceName={branchSourceName} />
  // A failed background revalidation keeps the restored repositories, so only surface the error
  // when there is nothing usable to show.
  if (query.isError && !query.data) throw query.error

  return <CreateBranchDialogInternalLoaded repos={query.data} repo={repo} owner={owner} {...props} />
}

type CreateBranchDialogInternalLoadedProps = Omit<CreateBranchDialogInternalProps, 'branchSourceName'> & {
  repos: CreateBranchDialogReposServerQueryQuery
}

const CreateBranchDialogInternalLoaded = ({
  repos,
  issueId,
  setCreateBranchInput,
  setBranchSourceName,
  selectedBranchName,
}: CreateBranchDialogInternalLoadedProps) => {
  const topRepositories = readTopRepositories(repos.viewer)

  const initialRepository = repos.repository ?? null
  const [selectedRepository, setSelectedRepository] = useState<RepositoryPickerRepository | null | undefined>(
    initialRepository,
  )

  const onSelectedRepository = useCallback((repository?: RepositoryPickerRepository) => {
    setSelectedRepository(repository)
  }, [])

  const onSelectRepository = useCallback(
    (selectedRepo?: RepositoryPickerRepository) => {
      if (selectedRepo && selectedRepo !== selectedRepository) {
        onSelectedRepository(selectedRepo)
      }
    },
    [onSelectedRepository, selectedRepository],
  )

  if (!selectedRepository) return null

  return (
    <CreateBranchDialogInternalInputs
      selectedRepository={selectedRepository}
      onSelectRepository={onSelectRepository}
      topRepositories={topRepositories}
      setCreateBranchInput={setCreateBranchInput}
      issueId={issueId}
      selectedBranchName={selectedBranchName}
      setBranchSourceName={setBranchSourceName}
    />
  )
}

type CreateBranchDialogInternalInputsProps = {
  selectedRepository: RepositoryPickerRepository
  onSelectRepository: (repo?: RepositoryPickerRepository) => void
  issueId: string
  topRepositories: RepositoryPickerRepository[]
  setCreateBranchInput: (request: CreateLinkedBranchRequest | null) => void
  selectedBranchName: string
  setBranchSourceName: (name: string | null) => void
}

const CreateBranchDialogInternalInputs = ({selectedRepository, ...props}: CreateBranchDialogInternalInputsProps) => {
  const owner = selectedRepository.owner.login
  const repo = selectedRepository.name
  const {sendAnalyticsEvent} = useAnalytics()
  const persister = useMemo(
    () =>
      createServerPersistedQueryPersister({
        queryHash: CreateBranchDialogFindRepoBranchesServerQuery,
        sendAnalyticsEvent,
        maxAgeMs: ISSUE_VIEWER_CACHE_TTL_MS,
        // Restored branches may be stale within their TTL, so revalidate after restoring them.
        alwaysRefetch: true,
      }),
    [sendAnalyticsEvent],
  )
  const query = useQuery({
    queryKey: createBranchDialogBranchesQueryKey(owner, repo),
    queryFn: ({signal}) => fetchCreateBranchDialogBranches(owner, repo, signal),
    placeholderData: previousData => previousData,
    gcTime: ISSUE_VIEWER_CACHE_TTL_MS,
    persister,
  })

  if (query.isPending || query.isPlaceholderData) {
    const branchSourceName = query.data?.repository?.defaultBranchRef?.name ?? null
    return <CreateBranchDialogInternalInputsLoading branchSourceName={branchSourceName} />
  }
  // A failed background revalidation keeps the restored branches, so only surface the error
  // when there is nothing usable to show.
  if (query.isError && !query.data) throw query.error

  return <CreateBranchDialogInternalInputsLoaded data={query.data} selectedRepository={selectedRepository} {...props} />
}

type CreateBranchDialogInternalInputsLoadedProps = CreateBranchDialogInternalInputsProps & {
  data: CreateBranchDialogFindRepoBranchesServerQueryQuery
}

const CreateBranchDialogInternalInputsLoaded = ({
  data,
  selectedRepository,
  topRepositories,
  onSelectRepository,
  setCreateBranchInput,
  issueId,
  selectedBranchName,
  setBranchSourceName,
}: CreateBranchDialogInternalInputsLoadedProps) => {
  const repository = data.repository !== undefined ? data.repository : null
  const defaultBranchRef = repository?.defaultBranchRef ?? null
  const initialSourceBranch = readRef(defaultBranchRef)

  const [selectedSourceBranch, setSelectedSourceBranch] = useState<BranchPickerBranch | null | undefined>(
    initialSourceBranch,
  )

  // Tracks whether the reader picked a source branch themselves, so revalidated data never
  // overwrites their choice.
  const sourceBranchPickedByReader = useRef(false)

  const resetSelectedSourceBranchAndName = useEffectEvent(() => {
    sourceBranchPickedByReader.current = false
    setSelectedSourceBranch(initialSourceBranch)
    setBranchSourceName(initialSourceBranch?.name ?? null)
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    resetSelectedSourceBranchAndName()
    // we want to reset selected source branch and name when repository changes
  }, [selectedRepository])

  const adoptRevalidatedDefaultBranch = useEffectEvent(() => {
    if (sourceBranchPickedByReader.current) return
    setSelectedSourceBranch(initialSourceBranch)
    setBranchSourceName(initialSourceBranch?.name ?? null)
  })

  useEffect(() => {
    // The default branch can be restored from a cache entry written up to the TTL ago, by which
    // time its tip may have moved on. Creating from that stale commit would silently branch from
    // the wrong place, so adopt the revalidated default once it arrives.

    adoptRevalidatedDefaultBranch()
  }, [initialSourceBranch?.id, initialSourceBranch?.target?.oid])

  const onSelectedSourceBranch = useCallback(
    (selectedBranches?: BranchPickerBranch[]) => {
      const branch = selectedBranches && selectedBranches.length > 0 ? selectedBranches[0] : null
      if (branch) sourceBranchPickedByReader.current = true
      setSelectedSourceBranch(current => branch ?? current)
      setBranchSourceName(initialSourceBranch?.name ?? null)
    },
    [initialSourceBranch?.name, setBranchSourceName],
  )

  const createBranchInput = useMemo(() => {
    if (!selectedRepository || !selectedSourceBranch || !selectedSourceBranch.target?.oid) {
      return null
    }
    return {
      input: {
        repositoryId: selectedRepository.id,
        name: `refs/heads/${selectedBranchName}`,
        oid: selectedSourceBranch.target.oid,
        issueId,
      },
      repositoryNameWithOwner:
        selectedRepository.nameWithOwner ?? `${selectedRepository.owner.login}/${selectedRepository.name}`,
    }
  }, [issueId, selectedBranchName, selectedRepository, selectedSourceBranch])

  useEffect(() => {
    setCreateBranchInput(createBranchInput)
  }, [createBranchInput, setCreateBranchInput])

  const branchesData = repository?.refs ?? null
  const branchPickerBranches = useMemo(() => {
    const repoBranches = branchesData?.edges
      ?.flatMap(edge => (edge?.node ? [readRef(edge.node)] : []))
      .filter((branch): branch is BranchPickerBranch => Boolean(branch))
    return repoBranches || []
  }, [branchesData])

  return (
    <div data-testid={'cb-container'} className={styles.repositoryBranchRow}>
      <div data-testid={'repository'} className={styles.repositoryPickerContainer}>
        <div data-testid={'repository-picker'} className={styles.repositoryPickerHeader}>
          <span className={styles.fieldLabel}>Repository destination</span>
        </div>
        <Suspense fallback={<RepositoryPickerPlaceholder />}>
          <RepositoryPicker
            initialRepository={selectedRepository}
            topRepositories={topRepositories}
            onSelect={onSelectRepository}
            options={{
              includeForks: true,
            }}
          />
        </Suspense>
      </div>
      <div data-testid={'source-branch-picker'} className={styles.branchSourceContainer}>
        <span className={styles.fieldLabel}>Branch source</span>
        <Suspense fallback={<BranchPickerPlaceholder />}>
          <BranchPicker
            activeBranch={selectedSourceBranch}
            anchorElement={anchorProps => (
              <DefaultBranchPickerAnchor {...anchorProps} branchName={selectedSourceBranch?.name} />
            )}
            defaultBranchId={initialSourceBranch?.id}
            branches={branchPickerBranches}
            owner={selectedRepository.owner.login}
            repo={selectedRepository.name}
            onSelectionChange={onSelectedSourceBranch}
            title={'Choose a source branch'}
          />
        </Suspense>
      </div>
    </div>
  )
}

type CreateBranchDialogInternalInputsLoadingProps = {
  branchSourceName: string | null
}

const CreateBranchDialogInternalInputsLoading = ({branchSourceName}: CreateBranchDialogInternalInputsLoadingProps) => {
  return (
    <div data-testid={'cb-container'} className={styles.loadingRepositoryBranchRow}>
      <div data-testid={'repository'} className={styles.branchNameContainer}>
        <div data-testid={'repository-picker'} className={styles.repositoryPickerHeader}>
          <span className={styles.fieldLabel}>Repository destination</span>
        </div>
        <RepositoryPickerPlaceholder />
      </div>
      <div data-testid={'source-branch-picker'} className={styles.loadingBranchSourceContainer}>
        <span className={styles.fieldLabel}>Branch source</span>
        <BranchPickerPlaceholder currentBranch={branchSourceName} />
      </div>
    </div>
  )
}
